/**
 * Expedientes de Legal Flow desde las RPC del servidor (sozu-supabase-migrations
 * 20261017233100–233500): forma de las filas, argumentos de cada llamada y métricas del Panel.
 * Sin Supabase, para probarlo solo.
 *
 * Las RPC ya resuelven la etapa (la misma que armaban los hooks del navegador), la fecha límite,
 * la prioridad y el responsable; aquí solo se traducen a camelCase y se dejan listas para pintar.
 */
import type { CaseStatus } from '@/types/legal-flow';

/**
 * `archivados`: propiedades Vendidas (Expedientes archivados). `archivadas`: solicitudes archivadas a
 * mano desde Legal Flow (20261022235141); los demás universos ya no las traen.
 */
export type UniversoExpedientes = 'pipeline' | 'apartado' | 'archivados' | 'archivadas';

export type OrdenExpedientes =
  | 'sla' | 'cuenta' | 'proyecto' | 'dueno' | 'comprador' | 'prioridad' | 'etapa' | 'agente'
  /** Fecha de compra (20261018030100); sin fecha, al final. */
  | 'compra'
  /** Fecha en que se archivó a mano (20261022235141). */
  | 'archivada';

export type PrioridadExpediente = 'Alto' | 'Medio' | 'Bajo';

/** Etapas del tablero, en el orden del pipeline. */
export const ETAPAS_PIPELINE: CaseStatus[] = [
  'Solicitud recibida', 'En revisión legal', 'Aprobado', 'Firma cliente', 'Firma titular', 'Firmado',
];

/** Etapas que ya no corren SLA. */
export const ETAPAS_TERMINALES: string[] = ['Firmado', 'Archivado', 'Cancelado', 'Rechazado'];

/** Quién archivó a mano una solicitud, cuándo y por qué. */
export interface ArchivoSolicitud {
  en: string;
  motivo: string;
  por: string;
}

export interface CompradorExpediente {
  idPersona: number;
  nombre: string;
  tipoPersona: string;
  porcentaje: number | null;
}

/** Una fila de `legal_flow_expedientes_listar` (y las tarjetas del resumen). Sin contacto. */
export interface ExpedienteFila {
  idCuentaCobranza: number;
  /** CC-000123: también es el id de la ruta del detalle. */
  folio: string;
  etapa: string;
  idPropiedad: number | null;
  idEstatusDisponibilidad: number | null;
  estatusPropiedad: string | null;
  numeroPropiedad: string | null;
  /** "Unidad 101", o null si la propiedad no tiene número. */
  unidad: string | null;
  idProyecto: number | null;
  /** "Sin proyecto" cuando el modelo no cuelga de un proyecto. */
  proyecto: string;
  modelo: string | null;
  agente: string | null;
  /** "Sozu", la inmobiliaria, o null = agente independiente. */
  empresa: string | null;
  idPersonaTitular: number | null;
  titular: string | null;
  /** El titular de la oferta primero. */
  compradores: CompradorExpediente[];
  precioFinal: number | null;
  fechaCompra: string | null;
  fechaCreacion: string | null;
  fechaLimite: string;
  prioridad: PrioridadExpediente;
  /** SLA vencido en una etapa que todavía corre (no Firmado ni Archivado). */
  vencido: boolean;
  responsable: string | null;
  /** Archivada a mano; null si no (o si el servidor todavía no lo trae). */
  archivo: ArchivoSolicitud | null;
}

export interface PaginaExpedientes {
  total: number;
  filas: ExpedienteFila[];
}

export interface EtapaResumen {
  etapa: string;
  total: number;
  vencidos: number;
  porVencer: number;
  /** Las primeras del tablero, por SLA. */
  tarjetas: ExpedienteFila[];
}

export interface ResumenExpedientes {
  total: number;
  activos: number;
  vencidos: number;
  porVencer: number;
  sinResponsable: number;
  etapas: EtapaResumen[];
  /** De todo el universo, sin los filtros: alimenta el filtro y la carga por abogado. */
  responsables: { nombre: string; activos: number }[];
  /** Proyectos del universo sin filtros (20261018030000; vacío si el servidor aún no lo trae). */
  proyectos: { idProyecto: number | null; proyecto: string; total: number }[];
  /** Filtrados sin agente y comprados en el mes (null si el servidor aún no lo trae). */
  sinAgente: number | null;
  esteMes: number | null;
}

/** Filtros comunes a listado y resumen. Vacíos = sin filtrar. */
export interface FiltrosExpedientes {
  universo: UniversoExpedientes;
  etapas?: string[];
  busqueda?: string;
  responsable?: string;
  idProyecto?: number | null;
  prioridades?: string[];
  soloVencidos?: boolean;
}

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));
const texto = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
};

/** Ruta del detalle del expediente (el folio es el id de la ruta). */
export const rutaExpediente = (e: { folio: string }) => `/admin/legal-flow/cases/${e.folio}`;

export const esEtapaTerminal = (etapa: string | null | undefined): boolean =>
  !!etapa && ETAPAS_TERMINALES.includes(etapa);

export function aExpedienteFila(r: any): ExpedienteFila {
  const etapa = String(r?.etapa ?? '');
  const numeroPropiedad = texto(r?.numero_propiedad);
  const compradores: CompradorExpediente[] = (Array.isArray(r?.compradores) ? r.compradores : []).map((c: any) => ({
    idPersona: Number(c?.id_persona),
    nombre: texto(c?.nombre) ?? 'Sin nombre',
    tipoPersona: texto(c?.tipo_persona) ?? 'pf',
    porcentaje: num(c?.porcentaje),
  }));
  return {
    idCuentaCobranza: Number(r?.id_cuenta_cobranza),
    folio: String(r?.folio ?? ''),
    etapa,
    idPropiedad: num(r?.id_propiedad),
    idEstatusDisponibilidad: num(r?.id_estatus_disponibilidad),
    estatusPropiedad: texto(r?.estatus_propiedad),
    numeroPropiedad,
    unidad: numeroPropiedad ? `Unidad ${numeroPropiedad}` : null,
    idProyecto: num(r?.id_proyecto),
    proyecto: texto(r?.proyecto) ?? 'Sin proyecto',
    modelo: texto(r?.modelo),
    agente: texto(r?.agente),
    empresa: texto(r?.empresa),
    idPersonaTitular: num(r?.id_persona_titular),
    titular: texto(r?.titular),
    compradores,
    precioFinal: num(r?.precio_final),
    fechaCompra: texto(r?.fecha_compra),
    fechaCreacion: texto(r?.fecha_creacion),
    fechaLimite: String(r?.fecha_limite ?? ''),
    prioridad: (['Alto', 'Medio', 'Bajo'].includes(r?.prioridad) ? r.prioridad : 'Bajo') as PrioridadExpediente,
    vencido: r?.vencido === true && !esEtapaTerminal(etapa),
    responsable: texto(r?.responsable),
    archivo: archivoDeFila(r),
  };
}

/** El archivo de una fila del listado (`archivada_en/_motivo/_por`); null si no está archivada. */
function archivoDeFila(r: any): ArchivoSolicitud | null {
  const en = texto(r?.archivada_en);
  if (!en) return null;
  return { en, motivo: texto(r?.archivada_motivo) ?? '', por: texto(r?.archivada_por) ?? 'Sin dato' };
}

/** El archivo del detalle (`{en, motivo, por}`); null si no está archivada. */
export function aArchivoSolicitud(a: any): ArchivoSolicitud | null {
  const en = texto(a?.en);
  if (!en) return null;
  return { en, motivo: texto(a?.motivo) ?? '', por: texto(a?.por) ?? 'Sin dato' };
}

export function aPaginaExpedientes(j: any): PaginaExpedientes {
  return {
    total: Number(j?.total ?? 0),
    filas: (Array.isArray(j?.filas) ? j.filas : []).map(aExpedienteFila),
  };
}

export function aResumenExpedientes(j: any): ResumenExpedientes {
  return {
    total: Number(j?.total ?? 0),
    activos: Number(j?.activos ?? 0),
    vencidos: Number(j?.vencidos ?? 0),
    porVencer: Number(j?.por_vencer ?? 0),
    sinResponsable: Number(j?.sin_responsable ?? 0),
    etapas: (Array.isArray(j?.etapas) ? j.etapas : []).map((e: any) => ({
      etapa: String(e?.etapa ?? ''),
      total: Number(e?.total ?? 0),
      vencidos: Number(e?.vencidos ?? 0),
      porVencer: Number(e?.por_vencer ?? 0),
      tarjetas: (Array.isArray(e?.tarjetas) ? e.tarjetas : []).map(aExpedienteFila),
    })),
    responsables: (Array.isArray(j?.responsables) ? j.responsables : []).map((r: any) => ({
      nombre: String(r?.nombre ?? ''),
      activos: Number(r?.activos ?? 0),
    })),
    proyectos: (Array.isArray(j?.proyectos) ? j.proyectos : []).map((p: any) => ({
      idProyecto: num(p?.id_proyecto),
      proyecto: texto(p?.proyecto) ?? 'Sin proyecto',
      total: Number(p?.total ?? 0),
    })),
    sinAgente: num(j?.sin_agente),
    esteMes: num(j?.este_mes),
  };
}

const arreglo = (v?: string[]) => (v && v.length ? v : null);
const cadena = (v?: string) => (v && v.trim() ? v.trim() : null);

/** Argumentos de `legal_flow_expedientes_listar`. `pagina` empieza en 1. */
export function argsListarExpedientes(
  f: FiltrosExpedientes,
  orden: { clave: OrdenExpedientes; dir: 'asc' | 'desc' },
  pagina: number,
  porPagina: number,
): Record<string, unknown> {
  const limite = Math.min(Math.max(Math.trunc(porPagina) || 15, 1), 500);
  return {
    p_universo: f.universo,
    p_etapas: arreglo(f.etapas),
    p_busqueda: cadena(f.busqueda),
    p_responsable: cadena(f.responsable),
    p_id_proyecto: f.idProyecto ?? null,
    p_prioridades: arreglo(f.prioridades),
    p_solo_vencidos: !!f.soloVencidos,
    p_orden: orden.clave,
    p_direccion: orden.dir,
    p_limite: limite,
    p_desplazamiento: (Math.max(Math.trunc(pagina) || 1, 1) - 1) * limite,
  };
}

/** Argumentos de `legal_flow_pipeline_resumen` (etapas y prioridades no aplican: se cuentan todas). */
export function argsResumenExpedientes(f: FiltrosExpedientes, tarjetasPorEtapa = 0): Record<string, unknown> {
  return {
    p_universo: f.universo,
    p_busqueda: cadena(f.busqueda),
    p_responsable: cadena(f.responsable),
    p_id_proyecto: f.idProyecto ?? null,
    p_solo_vencidos: !!f.soloVencidos,
    p_tarjetas_por_etapa: Math.min(Math.max(Math.trunc(tarjetasPorEtapa) || 0, 0), 50),
  };
}

export interface MetricasPipeline {
  activos: number;
  porRevisar: number;
  enFirma: number;
  vencidos: number;
  firmados: number;
}

/**
 * KPIs del Panel a partir de las etapas que se están mostrando. Con el filtro de etapa del Panel
 * las demás vienen en cero (se aplica en el navegador, como antes), así que las métricas siguen al
 * filtro sin otra llamada.
 */
export function metricasDeEtapas(etapas: EtapaResumen[]): MetricasPipeline {
  const de = (e: string) => etapas.find((x) => x.etapa === e);
  const total = (e: string) => de(e)?.total ?? 0;
  const activas = etapas.filter((e) => !esEtapaTerminal(e.etapa));
  return {
    activos: activas.reduce((a, e) => a + e.total, 0),
    porRevisar: total('Solicitud recibida') + total('En revisión legal'),
    enFirma: total('Firma cliente') + total('Firma titular'),
    vencidos: activas.reduce((a, e) => a + e.vencidos, 0),
    firmados: total('Firmado'),
  };
}

/** KPI del Panel y lo que abre: las etapas detrás de la métrica (y si son solo las vencidas). */
export type MetricaPipeline = 'activos' | 'porRevisar' | 'enFirma' | 'vencidos' | 'firmados';

export const ETAPAS_DE_METRICA: Record<MetricaPipeline, { titulo: string; etapas: string[]; soloVencidos?: boolean }> = {
  activos: { titulo: 'Expedientes activos', etapas: ['Solicitud recibida', 'En revisión legal', 'Aprobado', 'Firma cliente', 'Firma titular'] },
  porRevisar: { titulo: 'Por revisar', etapas: ['Solicitud recibida', 'En revisión legal'] },
  enFirma: { titulo: 'En firma', etapas: ['Firma cliente', 'Firma titular'] },
  vencidos: { titulo: 'Vencidos', etapas: ['Solicitud recibida', 'En revisión legal', 'Aprobado', 'Firma cliente', 'Firma titular'], soloVencidos: true },
  firmados: { titulo: 'Firmados', etapas: ['Firmado'] },
};

/**
 * Filtros del listado detrás de un KPI, con los del Panel (búsqueda, responsable, vencidos) y, si
 * hay filtro de etapa, solo esa (la intersección: el KPI no puede abrir etapas que no se muestran).
 */
export function filtrosDeMetrica(m: MetricaPipeline, panel: FiltrosExpedientes, etapaFiltro?: string): FiltrosExpedientes {
  const def = ETAPAS_DE_METRICA[m];
  const etapas = etapaFiltro ? def.etapas.filter((e) => e === etapaFiltro) : def.etapas;
  return {
    ...panel,
    universo: 'pipeline',
    // Sin intersección no hay nada que mostrar: una etapa que no existe deja la lista vacía.
    etapas: etapas.length ? etapas : ['(ninguna)'],
    soloVencidos: !!panel.soloVencidos || !!def.soloVencidos,
  };
}

/** Deja en cero las etapas que no son la filtrada (el filtro de etapa del Panel). */
export function soloEtapa(etapas: EtapaResumen[], etapa: string | null | undefined): EtapaResumen[] {
  if (!etapa) return etapas;
  return etapas.map((e) => (e.etapa === etapa ? e : { ...e, total: 0, vencidos: 0, porVencer: 0, tarjetas: [] }));
}

/** Días entre hoy y la fecha límite: positivo = atraso, negativo = faltan. */
export function diasDeAtraso(fechaLimite: string, ahora: number = Date.now()): number {
  const t = new Date(fechaLimite).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((ahora - t) / 86_400_000);
}
